import { useState, useEffect } from "react";
import AdminLayout from "../../layouts/AdminLayout";
import API from "../../services/api";
import { useNavigate } from "react-router-dom";
import {
  CheckCircle2, X, RefreshCw, Clock, CalendarDays, Search,
  PartyPopper, PlayCircle, Check, Pencil, Trash2,
} from "lucide-react";

// ─── DESIGN TOKENS (matched to ProjectList.js) ────────────────────────────────
const T = {
  violet: "#5C2D91",
  violetMid: "#7B3FB5",
  violetLight: "#EDE6F7",
  violetFaint: "#F7F3FD",
  red: "#C8102E",
  redLight: "#FCEAED",
  orange: "#E8640A",
  orangeLight: "#FEF0E6",
  gold: "#C87800",
  goldLight: "#FEF5DC",
  green: "#1A7A42",
  greenLight: "#E2F5EC",
  bg: "#F4F0FA",
  surface: "#FFFFFF",
  surfaceAlt: "#FAF8FD",
  dark: "#1A0A2E",
  muted: "#6B5A8A",
  faint: "#C4B8DC",
  border: "rgba(92,45,145,0.1)",
  borderSolid: "#E8E0F5",
};

const fmtDate = (raw) => {
  if (!raw) return "—";
  const d = new Date(raw);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

// ─── Confirm Modal (same design as ProjectList.js) ────────────────────────────
function ConfirmModal({ modal, onClose }) {
  if (!modal) return null;
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9999,
      background: "rgba(26,10,46,0.45)", backdropFilter: "blur(3px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 20, animation: "pa-modal-fadein 0.18s ease",
    }}>
      <style>{`
        @keyframes pa-modal-fadein { from { opacity:0; transform:scale(0.94) } to { opacity:1; transform:scale(1) } }
      `}</style>
      <div style={{
        background: T.surface, borderRadius: 20, width: "100%", maxWidth: 420,
        boxShadow: "0 20px 60px rgba(92,45,145,0.22), 0 4px 16px rgba(0,0,0,0.10)",
        border: `1px solid ${T.borderSolid}`, overflow: "hidden",
      }}>
        <div style={{ height: 4, background: `linear-gradient(90deg,${T.violet},${T.red})` }} />
        <div style={{ padding: "28px 28px 24px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
            <div style={{
              width: 48, height: 48, borderRadius: 14, flexShrink: 0,
              background: T.redLight, border: `1.5px solid #FECDD3`,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}><Trash2 size={22} /></div>
            <div style={{ fontWeight: 800, fontSize: 16, color: T.dark, lineHeight: 1.25 }}>
              {modal.title}
            </div>
          </div>
          <p style={{ fontSize: 13.5, color: T.muted, lineHeight: 1.65, margin: "0 0 24px", fontWeight: 400 }}>
            {modal.message}
          </p>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button onClick={onClose} style={{
              padding: "10px 22px", borderRadius: 11, fontSize: 13, fontWeight: 600,
              background: T.surfaceAlt, color: T.muted,
              border: `1.5px solid ${T.borderSolid}`, cursor: "pointer",
              fontFamily: "Montserrat, sans-serif",
            }}>Cancel</button>
            <button
              onClick={() => { modal.onConfirm(); onClose(); }}
              style={{
                padding: "10px 22px", borderRadius: 11, fontSize: 13, fontWeight: 700,
                background: `linear-gradient(135deg,${T.red},#E02040)`,
                color: "#fff", border: "none", cursor: "pointer",
                fontFamily: "Montserrat, sans-serif",
                boxShadow: `0 4px 14px ${T.red}40`,
              }}
            >{modal.confirmLabel || "Confirm"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ProjectApproval() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [hov, setHov] = useState(null);
  const [approvingId, setApprovingId] = useState(null);
  const [page, setPage] = useState(1);
  const [toast, setToast] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);
  const PER_PAGE = 10;
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => { fetchPendingProjects(); }, []);

  // ── ALL BUSINESS LOGIC UNCHANGED ─────────────────────────────────────────
  const fetchPendingProjects = async () => {
    try {
      setLoading(true);
      const res = await API.get("/projects/pending/" + localStorage.getItem("id"));
      const all = res.data.projects || [];
      const pending = all.filter((p) => {
        const s = (p.status || "").toLowerCase().trim();
        return s === "pending";
      });
      setProjects(pending);
    } catch (err) {
      console.error("[ProjectApproval] fetch error:", err);
      showToast("Failed to load projects.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    if (approvingId === id) return;
    setApprovingId(id);
    try {
      const res = await API.put(`/projects/${id}/approve`, {});
      showToast(res.data.message || "Project approved and activated!", "success");
      setProjects((prev) => prev.filter((p) => p.id !== id));
      setPage(1);
    } catch (err) {
      console.error("[ProjectApproval] approve error:", err);
      showToast(err.response?.data?.message || "Failed to approve project.", "error");
    } finally {
      setApprovingId(null);
    }
  };

  const handleReject = (id) => {
    setConfirmModal({
      title: "Reject Project",
      message: "Are you sure you want to reject this project? This action cannot be undone.",
      confirmLabel: "Reject",
      onConfirm: async () => {
        try {
          await API.put(`/projects/${id}`, { status: "Rejected" });
          showToast("Project rejected successfully.", "success");
          setProjects((prev) => prev.filter((p) => p.id !== id));
          setPage(1);
        } catch (err) {
          console.error("[ProjectApproval] reject error:", err);
          showToast("Failed to reject project.", "error");
        }
      },
    });
  };

  const handleDelete = (id) => {
    setConfirmModal({
      title: "Delete Project",
      message: "Are you sure you want to delete this project? This action cannot be undone.",
      confirmLabel: "Delete",
      onConfirm: async () => {
        try {
          await API.delete(`/projects/${id}`);
          showToast("Project deleted successfully.", "success");
          setProjects((prev) => prev.filter((p) => p.id !== id));
          setPage(1);
        } catch (err) {
          console.error("[ProjectApproval] delete error:", err);
          showToast("Failed to delete project.", "error");
        }
      },
    });
  };

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };
  // ── END UNCHANGED LOGIC ───────────────────────────────────────────────────

  const filtered = projects.filter((p) => {
    const q = search.toLowerCase();
    return (
      !q ||
      (p.project_uid || "").toLowerCase().includes(q) ||
      (p.title || "").toLowerCase().includes(q) ||
      (p.service_type || "").toLowerCase().includes(q) ||
      (p.client_name || "").toLowerCase().includes(q)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const paged = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const stats = {
    pending: projects.length,
    today: projects.filter((p) => {
      if (!p.created_at) return false;
      return new Date(p.created_at).toDateString() === new Date().toDateString();
    }).length,
  };

  return (
    <AdminLayout>
      <style>{`
       @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');

       .pa-root * { font-family: 'Montserrat', sans-serif !important; }
.pa-uid     { font-family: 'Montserrat', sans-serif !important; }

        @keyframes pa-toast-in {
          from { opacity: 0; transform: translateY(12px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }

        .pa-row:hover { background: ${T.violetFaint} !important; }

        .pa-search:focus-within {
          border-color: ${T.violet} !important;
          box-shadow: 0 0 0 3px rgba(92,45,145,0.1) !important;
        }

        .pa-refresh:hover { opacity: 0.85; }
        .pa-refresh { transition: opacity 0.15s; cursor: pointer; }
        @keyframes pa-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

        .pa-pill { transition: all 0.15s ease !important; }
        .pa-pill:hover { transform: translateY(-1px); }

        @media (max-width: 1024px) {
          .pa-kpi-strip { grid-template-columns: repeat(2,1fr) !important; }
        }
        @media (max-width: 768px) {
          .pa-header    { flex-direction: column !important; gap: 14px !important; }
          .pa-kpi-strip { grid-template-columns: repeat(2,1fr) !important; }
          .pa-filter-row{ flex-direction: column !important; align-items: flex-start !important; }
          .pa-hide-md   { display: none !important; }
        }
        @media (max-width: 480px) {
          .pa-kpi-strip { grid-template-columns: 1fr 1fr !important; }
          .pa-hide-sm   { display: none !important; }
        }
      `}</style>

      <ConfirmModal modal={confirmModal} onClose={() => setConfirmModal(null)} />

      {/* ── TOAST ── */}
      {toast && (
        <div style={{
          position: "fixed", bottom: 28, right: 28, zIndex: 9999,
          background: toast.type === "success" ? T.green : T.red,
          color: "#fff", padding: "13px 22px", borderRadius: 13,
          fontSize: 13, fontWeight: 700,
          boxShadow: "0 8px 28px rgba(0,0,0,0.18)",
          display: "flex", alignItems: "center", gap: 9,
          animation: "pa-toast-in 0.25s ease", maxWidth: 360,
        }}>
          <span style={{ display: "inline-flex" }}>{toast.type === "success" ? <CheckCircle2 size={16} /> : <X size={16} />}</span>
          {toast.msg}
        </div>
      )}

      <div className="pa-root" style={{ padding: "28px 32px", background: T.bg, minHeight: "100vh" }}>

        {/* ── PAGE HEADER ── */}
        <div className="pa-header" style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 28, gap: 16 }}>
          <div>
            {/* Eyebrow */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <div style={{ width: 3, height: 18, borderRadius: 2, background: `linear-gradient(180deg,${T.gold},${T.orange})` }} />
              <span style={{ fontSize: 11, fontWeight: 700, color: T.muted, letterSpacing: "0.1em", textTransform: "uppercase" }}>
                Admin · Project Management
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 5 }}>
              <h1 style={{ fontWeight: 800, fontSize: 28, color: T.dark, margin: 0, letterSpacing: "-0.5px" }}>
                Project Approvals
              </h1>
              <span style={{
                background: `linear-gradient(135deg,${T.gold},#e69a0b)`,
                color: "#fff", fontSize: 9, fontWeight: 700,
                padding: "3px 10px", borderRadius: 20, letterSpacing: "0.1em",
                textTransform: "uppercase", boxShadow: `0 2px 8px ${T.gold}40`,
              }}>PENDING</span>
            </div>
            <p style={{ color: T.muted, fontSize: 13, margin: 0, fontWeight: 400 }}>
              Review and approve projects awaiting activation
            </p>
          </div>

          {/* CHANGE 2: Refresh button — exact same design as ProjectList.js */}
          <button
            className="pa-refresh"
            onClick={async () => {
              setRefreshing(true);
              const minSpin = new Promise(resolve => setTimeout(resolve, 700));
              await Promise.all([fetchPendingProjects(), minSpin]);
              setRefreshing(false);
            }}
            disabled={refreshing}
            style={{
              width: 40, height: 40, borderRadius: 11,
              border: `1.5px solid ${T.borderSolid}`,
              background: T.surface, color: T.muted,
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
              cursor: refreshing ? "not-allowed" : "pointer",
              opacity: refreshing ? 0.7 : 1,
            }}
            title="Refresh"
          >
            <span style={{
              display: "inline-flex",
              animation: refreshing ? "pa-spin 0.7s linear infinite" : "none",
            }}>
              <RefreshCw size={16} />
            </span>
          </button>
        </div>

        {/* ── CHANGE 1: KPI CARDS — exact same design as ProjectList.js ── */}
        <div className="pa-kpi-strip" style={{
          display: "grid", gridTemplateColumns: "repeat(3,1fr)",
          gap: 16, marginBottom: 24,
        }}>
          {[
            { label: "Awaiting Approval", value: stats.pending, icon: <Clock size={22} />, color: T.gold, bg: `linear-gradient(135deg,${T.goldLight},#FFF8E0)`, border: "#F0D888" },
            { label: "Added Today", value: stats.today, icon: <CalendarDays size={22} />, color: T.violet, bg: `linear-gradient(135deg,${T.violetLight},#F0E8FF)`, border: "#D8C8F5" },
            { label: "Search Results", value: filtered.length, icon: <Search size={22} />, color: T.orange, bg: `linear-gradient(135deg,${T.orangeLight},#FFF3E8)`, border: "#F8D5B0" },
          ].map((s) => (
            <div key={s.label} style={{
              background: T.surface,
              borderRadius: 16,
              border: `1px solid ${T.borderSolid}`,
              padding: "20px 22px",
              display: "flex", alignItems: "center", gap: 14,
              boxShadow: "0 2px 12px rgba(92,45,145,0.06)",
              position: "relative", overflow: "hidden",
            }}>
              {/* Background accent blob */}
              <div style={{
                position: "absolute", right: -10, top: -10,
                width: 70, height: 70, borderRadius: "50%",
                background: s.bg, opacity: 0.6,
              }} />
              <div style={{
                width: 46, height: 46, borderRadius: 13,
                background: s.bg,
                border: `1.5px solid ${s.border}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 20, flexShrink: 0, position: "relative",
              }}>{s.icon}</div>
              <div style={{ position: "relative" }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: T.muted, marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  {s.label}
                </div>
                <div style={{ fontWeight: 800, fontSize: 28, color: s.color, lineHeight: 1 }}>
                  {s.value}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ── MAIN TABLE CARD ── */}
        <div style={{
          background: T.surface,
          borderRadius: 20,
          border: `1px solid ${T.borderSolid}`,
          overflow: "hidden",
          boxShadow: "0 4px 24px rgba(92,45,145,0.08)",
        }}>
          <div style={{ height: 4, background: `linear-gradient(90deg,${T.gold},${T.orange},${T.violet})` }} />

          <div style={{ padding: "20px 24px" }}>

            {/* ── TABLE TOOLBAR ── */}
            <div className="pa-filter-row" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, gap: 14 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, color: T.dark }}>
                  Pending Projects
                </div>
                <div style={{ fontSize: 12, color: T.muted, marginTop: 3, fontWeight: 400 }}>
                  {filtered.length} of {projects.length} records
                  {search && <span style={{ color: T.violet, fontWeight: 600 }}> · filtered</span>}
                </div>
              </div>

              {/* CHANGE 6: Search — exact same design as ProjectList.js */}
              <div className="pa-search" style={{
                display: "flex", alignItems: "center", gap: 8,
                background: T.surfaceAlt,
                border: `1.5px solid ${T.borderSolid}`,
                borderRadius: 12, padding: "9px 14px",
                minWidth: 240, transition: "all 0.2s",
              }}>
                <Search size={15} color={T.faint} />
                <input
                  type="text"
                  placeholder="Search title, client, UID..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  style={{
                    border: "none", background: "transparent",
                    fontSize: 13, color: T.dark, outline: "none",
                    width: "100%", fontWeight: 400,
                  }}
                />
                {search && (
                  <button onClick={() => setSearch("")} style={{
                    background: T.violetLight, border: "none", color: T.violet,
                    cursor: "pointer", fontSize: 11, width: 18, height: 18,
                    borderRadius: "50%", display: "flex", alignItems: "center",
                    justifyContent: "center", fontWeight: 700, flexShrink: 0,
                  }}>✕</button>
                )}
              </div>
            </div>

            {/* ── TABLE ── */}
            {loading ? (
              <div style={{
                textAlign: "center", padding: "64px 0",
                display: "flex", flexDirection: "column",
                alignItems: "center", gap: 16,
              }}>
                <div style={{
                  width: 56, height: 56, borderRadius: 16,
                  background: T.goldLight,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <Clock size={24} color={T.gold} />
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: T.dark, marginBottom: 4 }}>Loading projects</div>
                  <div style={{ fontSize: 13, color: T.muted }}>Fetching pending approvals...</div>
                </div>
              </div>
            ) : paged.length === 0 ? (
              <div style={{
                textAlign: "center", padding: "64px 0",
                display: "flex", flexDirection: "column",
                alignItems: "center", gap: 16,
              }}>
                <div style={{
                  width: 64, height: 64, borderRadius: 18,
                  background: `linear-gradient(135deg,${T.goldLight},#FFF8E0)`,
                  border: `2px dashed ${T.faint}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <PartyPopper size={28} color={T.gold} />
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: T.dark, marginBottom: 6 }}>
                    {search ? "No matching projects" : "No pending approvals"}
                  </div>
                  <div style={{ fontSize: 13, color: T.muted, maxWidth: 300, lineHeight: 1.6 }}>
                    {search
                      ? `No results for "${search}". Try a different search term.`
                      : "All projects have been reviewed. Nothing pending right now."}
                  </div>
                  {search && (
                    <button onClick={() => setSearch("")} style={{
                      marginTop: 14,
                      background: `linear-gradient(135deg,${T.violet},${T.violetMid})`,
                      color: "#fff", border: "none", padding: "9px 20px",
                      borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: "pointer",
                    }}>Clear Search</button>
                  )}
                </div>
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                {/* CHANGE 8: Table — matched to ProjectList.js styling */}
                <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, minWidth: 860 }}>
                  <thead>
                    <tr style={{ background: T.violetFaint }}>
                      {[
                        { label: "#", center: true, cls: "" },
                        { label: "Project UID", center: false, cls: "" },
                        { label: "Project Name", center: false, cls: "" },
                        { label: "Segment", center: false, cls: "pa-hide-md" },
                        { label: "Client", center: false, cls: "pa-hide-sm" },
                        { label: "Created", center: false, cls: "pa-hide-md" },
                        { label: "Actions", center: false, cls: "" },
                      ].map((h, i) => (
                        <th key={h.label} className={h.cls} style={{
                          fontSize: 10, fontWeight: 700, color: T.muted,
                          textTransform: "uppercase", letterSpacing: "0.09em",
                          padding: "12px 16px",
                          textAlign: h.center ? "center" : "left",
                          borderBottom: `2px solid ${T.borderSolid}`,
                          borderTop: `1px solid ${T.borderSolid}`,
                          whiteSpace: "nowrap",
                          background: T.violetFaint,
                        }}>{h.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {paged.map((project, i) => {
                      const rowNum = (page - 1) * PER_PAGE + i + 1;
                      const isApproving = approvingId === project.id;
                      const isLast = i === paged.length - 1;

                      return (
                        <tr
                          key={project.id}
                          className="pa-row"
                          onMouseEnter={() => setHov(project.id)}
                          onMouseLeave={() => setHov(null)}
                          style={{
                            background: T.surface,
                            transition: "background 0.12s",
                            opacity: isApproving ? 0.6 : 1,
                          }}
                        >
                          {/* CHANGE 5: # column — same style as ProjectList.js */}
                          <td style={{ padding: "15px 16px", textAlign: "center", borderBottom: isLast ? "none" : `1px solid ${T.borderSolid}` }}>
                            <span style={{
                              display: "inline-flex", alignItems: "center", justifyContent: "center",
                              width: 26, height: 26, borderRadius: 8,
                              background: T.violetFaint,
                              fontWeight: 700, fontSize: 11, color: T.muted,
                            }}>{rowNum}</span>
                          </td>

                          {/* Project UID */}
                          <td style={{ padding: "15px 16px", borderBottom: isLast ? "none" : `1px solid ${T.borderSolid}` }}>
                            <span className="pa-uid" style={{
                              display: "inline-block",
                              background: T.violetFaint,
                              border: `1px solid ${T.borderSolid}`,
                              color: T.violet, fontWeight: 600, fontSize: 12,
                              padding: "4px 10px", borderRadius: 8,
                              letterSpacing: "0.04em",
                            }}>
                              {project.project_uid || `PRJ${String(project.id).padStart(3, "0")}`}
                            </span>
                          </td>

                          {/* Project Name */}
                          <td style={{ padding: "15px 16px", borderBottom: isLast ? "none" : `1px solid ${T.borderSolid}` }}>
                            <div style={{ fontWeight: 700, fontSize: 14, color: T.dark, marginBottom: 3 }}>
                              {project.title || "—"}
                            </div>
                          </td>

                          {/* Service Type */}
                          <td className="pa-hide-md" style={{ padding: "15px 16px", borderBottom: isLast ? "none" : `1px solid ${T.borderSolid}` }}>
                            <div style={{
                              fontSize: 11, color: T.muted, fontWeight: 500,
                              display: "inline-flex", alignItems: "center", gap: 5,
                            }}>
                              <span style={{ width: 4, height: 4, borderRadius: "50%", background: T.faint, display: "inline-block" }} />
                              {project.service_type || "—"}
                            </div>
                          </td>

                          <td className="pa-hide-sm" style={{ padding: "15px 16px", borderBottom: isLast ? "none" : `1px solid ${T.borderSolid}` }}>
                            <span style={{ fontSize: 13, color: T.dark, fontWeight: 500 }}>
                              {project.client_name || "—"}
                            </span>
                          </td>
                          {/* Created */}
                          <td className="pa-hide-md" style={{ padding: "15px 16px", borderBottom: isLast ? "none" : `1px solid ${T.borderSolid}` }}>
                            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: T.dark, fontWeight: 500 }}>
                                
                                {fmtDate(project.created_at)}
                              </div>
                            </div>
                          </td>

                          {/* Actions — business logic UNCHANGED */}
                          <td style={{ padding: "15px 16px", borderBottom: isLast ? "none" : `1px solid ${T.borderSolid}` }}>
                            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                              {/* Approve */}
                              <button
                                onClick={() => handleApprove(project.id)}
                                disabled={isApproving}
                                title="Approve Project"
                                style={{
                                  display: "inline-flex", alignItems: "center", gap: 4,
                                  background: isApproving ? T.surfaceAlt : T.greenLight,
                                  color: isApproving ? T.faint : T.green,
                                  border: `1px solid ${isApproving ? T.borderSolid : "#A7F3D0"}`,
                                  padding: "7px 13px", borderRadius: 9,
                                  fontSize: 11, fontWeight: 700,
                                  cursor: isApproving ? "not-allowed" : "pointer",
                                  transition: "all 0.18s", whiteSpace: "nowrap",
                                }}
                                onMouseEnter={(e) => { if (!isApproving) { e.currentTarget.style.background = T.green; e.currentTarget.style.color = "#fff"; } }}
                                onMouseLeave={(e) => { if (!isApproving) { e.currentTarget.style.background = T.greenLight; e.currentTarget.style.color = T.green; } }}
                              >
                                {isApproving ? <Clock size={12} /> : <><Check size={12} /> </>}
                              </button>

                              {/* Reject */}
                              <button
                                onClick={() => handleReject(project.id)}
                                disabled={isApproving}
                                title="Reject Project"
                                style={{
                                  display: "inline-flex", alignItems: "center", gap: 4,
                                  background: T.redLight, color: T.red,
                                  border: "1px solid #FECDD3",
                                  padding: "7px 13px", borderRadius: 9,
                                  fontSize: 11, fontWeight: 700,
                                  cursor: "pointer",
                                  transition: "all 0.18s", whiteSpace: "nowrap",
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = T.red; e.currentTarget.style.color = "#fff"; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = T.redLight; e.currentTarget.style.color = T.red; }}
                              >
                                <X size={12} />
                              </button>

                              {/* Edit */}
                              <button
                                onClick={() => navigate(`/project-add?edit_id=${project.id}`)}
                                disabled={isApproving}
                                title="Edit Details"
                                style={{
                                  background: T.violetLight, color: T.violet,
                                  border: `1px solid ${T.borderSolid}`,
                                  padding: "7px 16px", borderRadius: 10,
                                  fontSize: 12, fontWeight: 700, cursor: "pointer",
                                  transition: "all 0.15s", whiteSpace: "nowrap",
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = T.violet; e.currentTarget.style.color = "#fff"; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = T.violetLight; e.currentTarget.style.color = T.violet; }}
                              ><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg></button>

                              {/* Delete */}
                              <button
                                onClick={() => handleDelete(project.id)}
                                disabled={isApproving}
                                title="Delete Project"
                                style={{
                                  display: "inline-flex", alignItems: "center", gap: 4,
                                  background: T.redLight, color: T.red,
                                  border: "1px solid #F8C8D0",
                                  padding: "7px 13px", borderRadius: 9,
                                  fontSize: 11, fontWeight: 700,
                                  cursor: "pointer",
                                  transition: "all 0.18s", whiteSpace: "nowrap",
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = T.red; e.currentTarget.style.color = "#fff"; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = T.redLight; e.currentTarget.style.color = T.red; }}
                              >
                                <Trash2 size={12} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* CHANGE 7: Pagination — matched to ProjectList.js design */}
            {!loading && filtered.length > 0 && (
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                marginTop: 20, paddingTop: 16,
                borderTop: `1px solid ${T.borderSolid}`,
                flexWrap: "wrap", gap: 12,
              }}>
                <span style={{ fontSize: 12, color: T.muted, fontWeight: 500 }}>
                  Showing{" "}
                  <strong style={{ color: T.dark }}>
                    {Math.min((page - 1) * PER_PAGE + 1, filtered.length)}–{Math.min(page * PER_PAGE, filtered.length)}
                  </strong>
                  {" "}of{" "}
                  <strong style={{ color: T.dark }}>{filtered.length}</strong>
                  {" "}projects
                </span>
                <div style={{ display: "flex", gap: 6 }}>
                  {/* Prev */}
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    style={{
                      height: 34, padding: "0 14px", borderRadius: 10,
                      border: `1.5px solid ${page === 1 ? T.borderSolid : T.violet}`,
                      background: page === 1 ? T.surface : T.violetLight,
                      color: page === 1 ? T.faint : T.violet,
                      cursor: page === 1 ? "not-allowed" : "pointer",
                      fontSize: 13, fontWeight: 700, transition: "all 0.15s",
                    }}
                  >‹ Prev</button>

                  {/* Page numbers */}
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .slice(Math.max(0, page - 3), Math.min(totalPages, page + 2))
                    .map((n) => (
                      <button key={n} onClick={() => setPage(n)} style={{
                        width: 34, height: 34, borderRadius: 10,
                        border: `1.5px solid ${n === page ? T.violet : T.borderSolid}`,
                        background: n === page ? `linear-gradient(135deg,${T.violet},${T.violetMid})` : T.surface,
                        color: n === page ? "#fff" : T.muted,
                        fontSize: 12, fontWeight: 700, cursor: "pointer",
                        boxShadow: n === page ? `0 2px 8px ${T.violet}40` : "none",
                        transition: "all 0.15s",
                      }}>{n}</button>
                    ))}

                  {/* Next */}
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    style={{
                      height: 34, padding: "0 14px", borderRadius: 10,
                      border: `1.5px solid ${page === totalPages ? T.borderSolid : T.violet}`,
                      background: page === totalPages ? T.surface : T.violetLight,
                      color: page === totalPages ? T.faint : T.violet,
                      cursor: page === totalPages ? "not-allowed" : "pointer",
                      fontSize: 13, fontWeight: 700, transition: "all 0.15s",
                    }}
                  >Next ›</button>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </AdminLayout>
  );
}